import { useState } from 'react';

interface FriendFarmProps {
  currentUser: { name: string; role: string } | null;
  onOpenAuth: () => void;
}

interface FriendFarmData {
  id: string;
  name: string;
  avatar: string;
  region: string;
  score: number;
  season: string;
  seasonEmoji: string;
  plots: { emoji: string; stage: number; needsWater: boolean; cropName: string }[];
  lastVisit: string;
  helpCount: number;
}

const FRIEND_FARMS: FriendFarmData[] = [
  {
    id: 'f1',
    name: '小花（湖南）',
    avatar: 'https://public.readdy.ai/ai/img_res/4ce71b521ebbea06a8fb8bec40859410.jpg',
    region: '湖南农村',
    score: 340,
    season: '春季',
    seasonEmoji: '🌸',
    plots: [
      { emoji: '🥕', stage: 4, needsWater: false, cropName: '胡萝卜' },
      { emoji: '🌿', stage: 2, needsWater: true, cropName: '草莓' },
      { emoji: '🌱', stage: 1, needsWater: true, cropName: '豌豆' },
      { emoji: '🍓', stage: 4, needsWater: false, cropName: '草莓' },
      { emoji: '🟫', stage: 0, needsWater: false, cropName: '' },
      { emoji: '🌿', stage: 2, needsWater: false, cropName: '胡萝卜' },
    ],
    lastVisit: '2小时前',
    helpCount: 12,
  },
  {
    id: 'f2',
    name: '阿强（云南）',
    avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20rural%20boy%20student%20portrait%2C%20simple%20clothes%2C%20hopeful%20expression%2C%20clean%20background&width=60&height=60&seq=ff002&orientation=squarish',
    region: '云南农村',
    score: 520,
    season: '夏季',
    seasonEmoji: '☀️',
    plots: [
      { emoji: '🍉', stage: 4, needsWater: false, cropName: '西瓜' },
      { emoji: '🌽', stage: 3, needsWater: true, cropName: '玉米' },
      { emoji: '🍅', stage: 4, needsWater: false, cropName: '番茄' },
      { emoji: '🌿', stage: 2, needsWater: true, cropName: '玉米' },
      { emoji: '🌱', stage: 1, needsWater: false, cropName: '番茄' },
      { emoji: '🟫', stage: 0, needsWater: false, cropName: '' },
    ],
    lastVisit: '昨天',
    helpCount: 28,
  },
  {
    id: 'f3',
    name: '陈大学姐（清华）',
    avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20female%20university%20student%20portrait%2C%20confident%20smile%2C%20clean%20background%2C%20academic%20look&width=60&height=60&seq=ff003&orientation=squarish',
    region: '北京',
    score: 1240,
    season: '秋季',
    seasonEmoji: '🍂',
    plots: [
      { emoji: '🍇', stage: 4, needsWater: false, cropName: '葡萄' },
      { emoji: '🎃', stage: 3, needsWater: false, cropName: '南瓜' },
      { emoji: '🍎', stage: 4, needsWater: false, cropName: '苹果' },
      { emoji: '🌿', stage: 2, needsWater: true, cropName: '葡萄' },
      { emoji: '🍄', stage: 3, needsWater: true, cropName: '蘑菇' },
      { emoji: '🌱', stage: 1, needsWater: false, cropName: '苹果' },
    ],
    lastVisit: '3天前',
    helpCount: 67,
  },
  {
    id: 'f4',
    name: '苗家小妹（贵州）',
    avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20Miao%20ethnic%20girl%20student%20portrait%2C%20traditional%20elements%2C%20warm%20smile%2C%20clean%20background&width=60&height=60&seq=ff004&orientation=squarish',
    region: '贵州农村',
    score: 680,
    season: '冬季',
    seasonEmoji: '❄️',
    plots: [
      { emoji: '🥬', stage: 4, needsWater: false, cropName: '白菜' },
      { emoji: '🌱', stage: 1, needsWater: true, cropName: '萝卜' },
      { emoji: '🌼', stage: 3, needsWater: true, cropName: '雪莲花' },
      { emoji: '🥬', stage: 2, needsWater: false, cropName: '白菜' },
      { emoji: '🟫', stage: 0, needsWater: false, cropName: '' },
      { emoji: '🌲', stage: 2, needsWater: true, cropName: '松果' },
    ],
    lastVisit: '5小时前',
    helpCount: 34,
  },
];

export default function FriendFarm({ currentUser, onOpenAuth }: FriendFarmProps) {
  const [visitingFarm, setVisitingFarm] = useState<FriendFarmData | null>(null);
  const [wateredPlots, setWateredPlots] = useState<Record<string, Set<number>>>({});
  const [notification, setNotification] = useState<string | null>(null);
  const [helpCounts, setHelpCounts] = useState<Record<string, number>>(
    Object.fromEntries(FRIEND_FARMS.map(f => [f.id, f.helpCount]))
  );

  const showNotif = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 2500);
  };

  const handleVisit = (farm: FriendFarmData) => {
    if (!currentUser) { onOpenAuth(); return; }
    setVisitingFarm(farm);
  };

  const handleWaterPlot = (farmId: string, plotIdx: number, cropName: string) => {
    if (!currentUser) { onOpenAuth(); return; }
    setWateredPlots(prev => {
      const farmSet = new Set(prev[farmId] || []);
      farmSet.add(plotIdx);
      return { ...prev, [farmId]: farmSet };
    });
    setHelpCounts(prev => ({ ...prev, [farmId]: (prev[farmId] || 0) + 1 }));
    showNotif(`帮 ${visitingFarm?.name} 的 ${cropName} 浇水成功！你获得 +5 积分 💧`);
  };

  const handleWaterAll = (farm: FriendFarmData) => {
    if (!currentUser) { onOpenAuth(); return; }
    const needWaterIdxs = farm.plots
      .map((p, i) => ({ ...p, i }))
      .filter(p => p.needsWater && p.stage > 0)
      .map(p => p.i);
    if (needWaterIdxs.length === 0) { showNotif('所有作物都已浇过水了！'); return; }
    setWateredPlots(prev => {
      const farmSet = new Set([...(prev[farm.id] || []), ...needWaterIdxs]);
      return { ...prev, [farm.id]: farmSet };
    });
    setHelpCounts(prev => ({ ...prev, [farm.id]: (prev[farm.id] || 0) + needWaterIdxs.length }));
    showNotif(`一键帮 ${farm.name} 浇水 ${needWaterIdxs.length} 株！你获得 +${needWaterIdxs.length * 5} 积分 🌊`);
  };

  const seasonBgMap: Record<string, string> = {
    '春季': 'linear-gradient(135deg, #7B5E3A 0%, #9B7A52 50%, #7B5E3A 100%)',
    '夏季': 'linear-gradient(135deg, #6B4E1A 0%, #8B6A2A 50%, #6B4E1A 100%)',
    '秋季': 'linear-gradient(135deg, #8B5E2A 0%, #A0723A 50%, #8B5E2A 100%)',
    '冬季': 'linear-gradient(135deg, #5A6E7A 0%, #7A8E9A 50%, #5A6E7A 100%)',
  };

  const seasonGradMap: Record<string, string> = {
    '春季': 'from-pink-400 to-rose-400',
    '夏季': 'from-amber-400 to-orange-400',
    '秋季': 'from-orange-400 to-amber-500',
    '冬季': 'from-sky-400 to-cyan-400',
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <i className="ri-group-line text-green-600"></i>
              好友农场
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">帮好友浇水，双方都获得积分！</p>
          </div>
          <span className="text-xs bg-green-50 text-green-600 font-semibold px-2.5 py-1 rounded-full">
            浇水 +5积分
          </span>
        </div>
      </div>

      {notification && (
        <div className="bg-green-50 border-b border-green-100 px-4 py-2 flex items-center gap-2 text-green-700 text-xs font-medium">
          <i className="ri-checkbox-circle-line text-green-500"></i>
          {notification}
        </div>
      )}

      <div className="p-4 flex flex-col gap-3">
        {FRIEND_FARMS.map((farm) => {
          const wateredSet = wateredPlots[farm.id] || new Set<number>();
          const needWaterCount = farm.plots.filter((p, i) => p.needsWater && p.stage > 0 && !wateredSet.has(i)).length;
          return (
            <div key={farm.id} className="border border-gray-100 rounded-xl overflow-hidden hover:border-green-200 transition-all">
              <div className="flex items-center gap-3 p-3">
                <img src={farm.avatar} alt={farm.name} className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0 border-2 border-gray-100" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-bold text-gray-900 text-sm">{farm.name}</span>
                    <span className="text-xs">{farm.seasonEmoji}</span>
                    <span className="text-xs text-gray-400">{farm.season}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-400">{farm.region}</span>
                    <span className="text-xs text-amber-600 font-semibold">{farm.score}分</span>
                    {needWaterCount > 0 && (
                      <span className="text-xs bg-orange-100 text-orange-600 font-semibold px-1.5 py-0.5 rounded-full">
                        {needWaterCount}株需浇水
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    onClick={() => handleVisit(farm)}
                    className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-full cursor-pointer hover:bg-green-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-eye-line mr-1"></i>参观
                  </button>
                  {needWaterCount > 0 && (
                    <button
                      onClick={() => handleWaterAll(farm)}
                      className="px-3 py-1.5 bg-sky-500 text-white text-xs font-semibold rounded-full cursor-pointer hover:bg-sky-600 transition-colors whitespace-nowrap"
                    >
                      💧 一键浇水
                    </button>
                  )}
                </div>
              </div>

              {/* Mini farm preview */}
              <div className="px-3 pb-3">
                <div className="rounded-lg p-1.5 grid grid-cols-6 gap-1" style={{ background: seasonBgMap[farm.season] }}>
                  {farm.plots.map((plot, idx) => {
                    const alreadyWatered = wateredSet.has(idx);
                    const showWaterBadge = plot.needsWater && plot.stage > 0 && !alreadyWatered;
                    return (
                      <button
                        key={idx}
                        onClick={() => plot.needsWater && plot.stage > 0 && !alreadyWatered && handleWaterPlot(farm.id, idx, plot.cropName)}
                        className={`relative aspect-square rounded flex items-center justify-center text-sm transition-all ${
                          plot.stage === 4 ? 'bg-yellow-100 border border-yellow-300' :
                          plot.stage === 0 ? 'bg-amber-100/50' :
                          showWaterBadge ? 'bg-green-100 border border-orange-300 cursor-pointer hover:bg-orange-50' :
                          'bg-green-100 border border-green-200'
                        }`}
                        title={plot.cropName || '空地'}
                      >
                        {showWaterBadge && (
                          <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-400 rounded-full"></div>
                        )}
                        {alreadyWatered && plot.stage > 0 && (
                          <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-sky-400 rounded-full"></div>
                        )}
                        <span className="text-sm leading-none">{plot.emoji}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-gray-400">上次访问：{farm.lastVisit}</span>
                  <span className="text-[10px] text-green-600 font-semibold">
                    <i className="ri-drop-line"></i> 已帮助浇水 {helpCounts[farm.id]} 次
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Visit Modal */}
      {visitingFarm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl">
            <div className={`bg-gradient-to-r ${seasonGradMap[visitingFarm.season]} px-5 py-4 text-white`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={visitingFarm.avatar} alt={visitingFarm.name} className="w-10 h-10 rounded-full object-cover object-top border-2 border-white/50" />
                  <div>
                    <p className="font-black text-base">{visitingFarm.name} 的农场</p>
                    <p className="text-white/80 text-xs">{visitingFarm.seasonEmoji} {visitingFarm.season} · {visitingFarm.score}积分</p>
                  </div>
                </div>
                <button onClick={() => setVisitingFarm(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 cursor-pointer">
                  <i className="ri-close-line text-white"></i>
                </button>
              </div>
            </div>

            <div className="p-5">
              {notification && (
                <div className="bg-green-50 rounded-xl px-3 py-2 mb-3 flex items-center gap-2 text-green-700 text-xs font-medium">
                  <i className="ri-checkbox-circle-line text-green-500"></i>
                  {notification}
                </div>
              )}

              <div className="rounded-2xl p-3 grid grid-cols-3 gap-2 mb-4" style={{ background: seasonBgMap[visitingFarm.season] }}>
                {visitingFarm.plots.map((plot, idx) => {
                  const alreadyWatered = (wateredPlots[visitingFarm.id] || new Set()).has(idx);
                  const canWater = plot.needsWater && plot.stage > 0 && !alreadyWatered;
                  return (
                    <button
                      key={idx}
                      onClick={() => canWater && handleWaterPlot(visitingFarm.id, idx, plot.cropName)}
                      className={`relative aspect-square rounded-xl flex flex-col items-center justify-center gap-1 transition-all border-2 ${
                        plot.stage === 4 ? 'bg-yellow-100 border-yellow-400' :
                        plot.stage === 0 ? 'bg-amber-100/60 border-amber-300/50' :
                        canWater ? 'bg-green-100 border-orange-300 cursor-pointer hover:bg-orange-50 hover:scale-105' :
                        'bg-green-100 border-green-300'
                      }`}
                    >
                      {canWater && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-400 rounded-full flex items-center justify-center">
                          <span className="text-[8px]">💧</span>
                        </div>
                      )}
                      {alreadyWatered && plot.stage > 0 && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-sky-400 rounded-full flex items-center justify-center">
                          <i className="ri-check-line text-white text-[8px]"></i>
                        </div>
                      )}
                      <span className="text-2xl leading-none">{plot.emoji}</span>
                      <span className="text-[9px] text-gray-600 font-medium text-center px-1">
                        {plot.stage === 0 ? '空地' : plot.stage === 4 ? plot.cropName : canWater ? '点击浇水' : plot.cropName}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleWaterAll(visitingFarm)}
                  className="flex-1 py-2.5 bg-sky-500 text-white text-sm font-bold rounded-full cursor-pointer hover:bg-sky-600 transition-colors whitespace-nowrap"
                >
                  💧 一键全部浇水
                </button>
                <button
                  onClick={() => setVisitingFarm(null)}
                  className="px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-full cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  离开
                </button>
              </div>
              <p className="text-center text-xs text-gray-400 mt-2">帮好友浇水每株 +5 积分，好友也会收到通知</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
